export const COMMON_CONDITIONS = [
  "poisoned",
  "stunned",
  "frightened",
  "blessed",
  "hasted",
  "shielded",
  "prone",
  "hidden",
  "concentrated",
] as const;

type Severity = "debuff" | "buff" | "neutral";

const DEBUFFS = new Set(["poisoned", "stunned", "frightened"]);
const BUFFS = new Set(["blessed", "hasted", "shielded"]);

function normalizeCondition(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      typeof entry === "string"
        ? entry
            .split(/[;,]/g)
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    );
  }
  if (!value || typeof value !== "string") return [];
  return value
    .split(/[;,]/g)
    .map((entry) => entry.trim())
    .map((entry) => entry.toLowerCase())
    .filter(Boolean);
}

function normalizeConditionSet(values: unknown): string[] {
  const raw = Array.isArray(values) ? values : normalizeCondition(values);
  const out: string[] = [];
  for (const value of raw) {
    const key = String(value).trim().toLowerCase();
    if (!key) continue;
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

function toggleConditionValue(values: string[], condition: string): string[] {
  const key = condition.trim().toLowerCase();
  if (!key) return values;

  if (values.includes(key)) {
    return values.filter((entry) => entry !== key);
  }
  return [...values, key];
}

function severityFor(condition: string): Severity {
  if (DEBUFFS.has(condition)) return "debuff";
  if (BUFFS.has(condition)) return "buff";
  return "neutral";
}

function badgeStyle(condition: string) {
  const severity = severityFor(condition);
  if (severity === "debuff") {
    return {
      background: "rgba(248, 113, 113, 0.18)",
      border: "1px solid rgba(248, 113, 113, 0.5)",
      color: "rgba(254, 226, 226, 1)",
    };
  }
  if (severity === "buff") {
    return {
      background: "rgba(134, 239, 172, 0.18)",
      border: "1px solid rgba(134, 239, 172, 0.5)",
      color: "rgba(187, 247, 208, 1)",
    };
  }
  return {
    background: "rgba(148, 163, 184, 0.22)",
    border: "1px solid rgba(148, 163, 184, 0.5)",
    color: "rgba(226, 232, 240, 1)",
  };
}

function abbreviateCondition(condition: string) {
  if (condition === "concentrated") return "CONC";
  if (condition === "frightened") return "FRG";
  return condition.slice(0, 3).toUpperCase();
}

export function parseConditions(value: unknown): string[] {
  return normalizeConditionSet(value);
}

export function setConditionsValue(
  current: unknown,
  condition: string,
): string {
  return toggleConditionValue(normalizeConditionSet(current), condition).join(
    ", ",
  );
}

export function ConditionBadges({
  conditions,
  onToggle,
  interactive = false,
}: {
  conditions: string[];
  interactive?: boolean;
  onToggle?: (condition: string) => void;
}) {
  if (conditions.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
      }}
    >
      {conditions.map((condition) => {
        const style = badgeStyle(condition);
        return (
          <span
            key={condition}
            title={condition}
            onClick={
              interactive && onToggle ? () => onToggle(condition) : undefined
            }
            style={{
              borderRadius: 999,
              padding: "2px 6px",
              fontSize: 10,
              lineHeight: 1.05,
              textTransform: "uppercase",
              letterSpacing: 0.35,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              cursor: interactive ? "pointer" : "default",
              ...style,
            }}
          >
            {abbreviateCondition(condition)}
          </span>
        );
      })}
    </div>
  );
}
